import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import CountUp from "react-countup";
import { useInView } from "react-intersection-observer";
import emailjs from "@emailjs/browser";
import { Helmet } from "react-helmet-async";
import "./Home.css";

// ---- Hero Slider ----
const heroSlides = [
  {
    image: "/Slider1.webp", // ← keep as is (already static)
    title: "Transform Spaces with Timeless Glass Elegance",
    subtitle: "Exceptional Craftsmanship. Contemporary Design.",
  },
  {
    image:
      "https://res.cloudinary.com/dndg4fwia/image/upload/v1772597384/996zntpnvdrmw0cwpw4bepn19m.png",
    title: "Where Art Meets Glass",
    subtitle: "Handcrafted Creations Shaped with Passion Since 1997",
  },
  {
    image:
      "https://res.cloudinary.com/dndg4fwia/image/upload/v1772568285/DSCN2389.jpg",
    title: "Excellence in Blown Glass Manufacturing",
    subtitle: "Proudly Crafted in Firozabad, India",
  },
];

const HeroSlider = () => {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const timer = setInterval(
      () => setCurrent((c) => (c + 1) % heroSlides.length),
      5000,
    );
    return () => clearInterval(timer);
  }, []);

  const prevSlide = () => {
    setCurrent((c) => (c === 0 ? heroSlides.length - 1 : c - 1));
  };

  const nextSlide = () => {
    setCurrent((c) => (c + 1) % heroSlides.length);
  };

  return (
    <section className="hero">
      {heroSlides.map((slide, i) => (
        <div
          key={i}
          className={`hero__slide ${i === current ? "active" : ""}`}
          style={{ backgroundImage: `url(${slide.image})` }}
        >
          <div className="hero__overlay" />
          <div className="hero__content">
            <p className="hero__eyebrow">RK Overseas — Est. 1997</p>
            <h1 className="hero__title">{slide.title}</h1>
            <p className="hero__sub">{slide.subtitle}</p>
            <div className="hero__actions">
              <Link
                to="/product"
                className="btn-primary"
                aria-label="Show more products"
              >
                Explore Products
              </Link>
              <Link
                to="/contact"
                className="btn-outline"
                style={{ marginLeft: "16px" }}
              >
                Get In Touch
              </Link>
            </div>
          </div>
        </div>
      ))}

      {/* Arrows */}
      <button
        className="hero__arrow hero__arrow--left"
        onClick={prevSlide}
        aria-label="Previous slide"
      >
        &#10094;
      </button>
      <button
        className="hero__arrow hero__arrow--right"
        onClick={nextSlide}
        aria-label="Next slide"
      >
        &#10095;
      </button>

      {/* Dots */}
      <div className="hero__dots">
        {heroSlides.map((_, i) => (
          <button
            key={i}
            className={`hero__dot ${i === current ? "active" : ""}`}
            onClick={() => setCurrent(i)}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </div>
    </section>
  );
};

// ---- About Snippet ----
const AboutSnippet = () => (
  <section className="about-home section">
    <div className="container">
      <div className="about-home__grid">
        <div className="about-home__image">
          <img
            src="https://res.cloudinary.com/dndg4fwia/image/upload/q_auto,f_auto,/v1772602760/Rani_Pink_For_Abhinav_Mishra_%EF%B8%8F_This_is_the_most_special_evening_and_event_for_me_every_single_y.jpg"
            alt="RK Overseas Factory"
          />
          <div className="about-home__badge">
            <span className="about-home__badge-year">1997</span>
            <span className="about-home__badge-text">Est.</span>
          </div>
        </div>
        <div className="about-home__content">
          <p className="about-home__eyebrow">Who We Are</p>
          <h2>Crafting Timeless Glass Creations Since 1997</h2>
          <div className="section-divider" style={{ margin: "16px 0 22px" }} />
          <p>
            <strong>RK Overseas</strong> is a distinguished manufacturer and
            global exporter of premium home decorative accessories, proudly
            operating from Firozabad — the renowned glass city of India.
          </p>
          <p style={{ marginTop: "14px", color: "var(--text-muted)" }}>
            With a fully integrated in-house blown glass manufacturing facility,
            we specialize in creating exceptional handcrafted pieces that blend
            contemporary elegance with timeless artistry. Each product reflects
            precision, innovation, and a commitment to quality, offering designs
            suited for diverse lifestyles and international markets.
          </p>
          <div style={{ marginTop: "32px" }}>
            <Link
              to="/about"
              className="btn-primary"
              aria-label="Learn more about RK Overseas"
            >
              Learn More
            </Link>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// ---- Stats Counter ----
const stats = [
  { value: 25000, label: "Designs", suffix: "+" },
  { value: 28, label: "Year's Of Experience", suffix: "+" },
  { value: 150, label: "Clients", suffix: "+" },
];

const StatsSection = () => {
  const { ref, inView } = useInView({ triggerOnce: true });

  return (
    <section className="stats section" ref={ref}>
      <div className="container">
        <h2 className="section-title">Decades of Craftsmanship and Trust</h2>
        <div className="section-divider" />
        <div className="stats__grid">
          {stats.map((stat, i) => (
            <div className="stats__item" key={i}>
              <div className="stats__number">
                {inView ? (
                  <>
                    <CountUp end={stat.value} duration={2.5} />
                    <span className="stats__suffix">{stat.suffix}</span>
                  </>
                ) : (
                  <>
                    <span>0</span>
                    <span className="stats__suffix">{stat.suffix}</span>
                  </>
                )}
              </div>
              <div className="stats__label">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ---- Product Categories (uses real Cloudinary data) ----
import { homepageCategories } from "../data/index";

const ProductsSection = () => {
  return (
    <section
      className="products-home section"
      style={{ background: "var(--cream)" }}
    >
      <div className="container">
        <h2 className="section-title">Our Product Categories</h2>
        <div className="section-divider" />
        <p className="section-subtitle">
          A perfect amalgamation of contemporary and classic designs
        </p>
        <div className="products-home__grid">
          {homepageCategories.map((cat, i) => (
            <Link
              to={`/product?category=${encodeURIComponent(cat.name)}`}
              className="products-home__card"
              key={i}
              style={{ textDecoration: "none" }}
            >
              <div className="products-home__img-wrap">
                <img src={cat.image} alt={cat.name} loading="lazy" />
                <div className="products-home__overlay">
                  <span
                    className="btn-outline"
                    aria-label={`View more ${cat.name} products`}
                    style={{ fontSize: "0.75rem", padding: "10px 24px" }}
                  >
                    View More
                  </span>
                </div>
              </div>
              <h3 className="products-home__name">{cat.name}</h3>
            </Link>
          ))}
        </div>
        <div style={{ textAlign: "center", marginTop: "50px" }}>
          <Link
            to="/product"
            className="btn-primary"
            aria-label="Show more product categories"
          >
            Show More
          </Link>
        </div>
      </div>
    </section>
  );
};

// ---- Testimonials ----
const testimonials = [
  {
    text: "RK Overseas consistently delivers distinctive designs aligned with international market trends. Their product development and quality standards make them a reliable long-term sourcing partner.",
    name: "Daniel Sanders",
    company: "Sandezines, USA",
  },
  {
    text: "From concept to production, the experience with RK Overseas has always been seamless. Their innovation in blown glass and commitment to excellence truly set them apart.",
    name: "Michael Thompson",
    company: "AllStates, USA",
  },
  {
    text: "The versatility in design and consistent premium craftsmanship make RK Overseas a standout in the decorative glass industry. Their responsiveness and reliability are greatly appreciated.",
    name: "Emily Sullivan",
    company: "GlenHeaven, USA",
  },
  {
    text: "RK Overseas offers beautifully crafted collections that combine modern styling with timeless appeal. Their attention to quality and detail makes them a valued partner season after season.",
    name: "Andrew Collins",
    company: "Sullivans, USA",
  },
];

const TestimonialsSection = () => {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const t = setInterval(
      () => setCurrent((c) => (c + 1) % testimonials.length),
      5000,
    );
    return () => clearInterval(t);
  }, []);

  return (
    <section
      className="testimonials section"
      style={{ background: "var(--primary)" }}
    >
      <div className="container">
        <h2 className="section-title" style={{ color: "var(--white)" }}>
          Client Feedback
        </h2>
        <div className="section-divider" />
        <div className="testimonials__carousel">
          {testimonials.map((t, i) => (
            <div
              key={i}
              className={`testimonials__item ${i === current ? "active" : ""}`}
            >
              <p className="testimonials__quote">"{t.text}"</p>
              <div className="testimonials__author">
                <span className="testimonials__name">{t.name}</span>
                <span className="testimonials__company">{t.company}</span>
              </div>
            </div>
          ))}
        </div>
        <div className="testimonials__dots">
          {testimonials.map((_, i) => (
            <button
              key={i}
              className={`hero__dot ${i === current ? "active" : ""}`}
              onClick={() => setCurrent(i)}
              aria-label={`Go to testimonial ${i + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

// ---- Contact Form (mini) ----
const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

const ContactMini = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await emailjs.send(
        SERVICE_ID,
        TEMPLATE_ID,
        {
          from_name: form.name,
          name: form.name,
          email: form.email,
          phone: form.phone,
          message: form.message,
        },
        PUBLIC_KEY,
      );
      setStatus("success");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      setStatus("error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="contact-mini section">
      <div className="container">
        <h2 className="section-title">Reach Out to Us</h2>
        <div className="section-divider" />
        <form className="contact-mini__form" onSubmit={handleSubmit}>
          <div className="contact-mini__row">
            <input
              type="text"
              name="name"
              placeholder="Your Name"
              value={form.name}
              onChange={handleChange}
              required
            />
            <input
              type="email"
              name="email"
              placeholder="Your Email"
              value={form.email}
              onChange={handleChange}
              required
            />
          </div>
          <input
            type="tel"
            name="phone"
            placeholder="Phone Number"
            value={form.phone}
            onChange={handleChange}
          />
          <textarea
            name="message"
            placeholder="Your Message"
            rows={5}
            value={form.message}
            onChange={handleChange}
            required
          />
          {status === "success" && (
            <p className="form-success">
              ✅ Your message has been sent successfully! We'll get back to you
              soon!{" "}
            </p>
          )}
          {status === "error" && (
            <p className="form-error">
              Something went wrong. Please try again.
            </p>
          )}
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? "Sending..." : "Send Now"}
          </button>
        </form>
      </div>
    </section>
  );
};

// ---- Fairs & Exhibitions ----
const fairs = [
  {
    name: "IHGF Delhi Fair, Delhi, India",
    image:
      "https://res.cloudinary.com/dndg4fwia/image/upload/q_auto,f_auto,/v1772567657/123.jpg",
    tag: "IHGF",
  },
  {
    name: "JP Exhibition, Agra, India",
    image:
      "https://res.cloudinary.com/dndg4fwia/image/upload/q_auto,f_auto,/v1772567673/DSCN1039.jpg",
    tag: "JPE",
  },
  {
    name: "UP Internation Trade Fair, Delhi, India",
    image:
      "https://res.cloudinary.com/dndg4fwia/image/upload/q_auto,f_auto,/v1772595777/WhatsApp_Image_2026-03-04_at_9.10.43_AM.jpg",
    tag: "UPITS",
  },
];

const FairsSection = () => (
  <section className="fairs section" style={{ background: "var(--cream)" }}>
    <div className="container">
      <h2 className="section-title">Fairs & Exhibitions</h2>
      <div className="section-divider" />
      <div className="fairs__grid">
        {fairs.map((f, i) => (
          <div className="fairs__card" key={i}>
            <div className="fairs__img-wrap">
              <img src={f.image} alt={f.name} loading="lazy" />
              <div className="fairs__tag">{f.tag}</div>
            </div>
            <p className="fairs__name">{f.name}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ---- Awards ----
const awards = [
  {
    img: "https://res.cloudinary.com/dndg4fwia/image/upload/q_auto,f_auto,/v1772612578/ChatGPT_Image_Mar_4_2026_01_52_05_PM.png",
    text: "Receiving recognition award for outstanding contribution in Global Export.",
  },
];

const AwardsSection = () => (
  <section className="awards section">
    <div className="container">
      <h2 className="section-title">Awards & Achievements</h2>
      <div className="section-divider" />

      <div className="awards__grid">
        {awards.map((award, i) => (
          <div className="awards__img" key={i}>
            <img src={award.img} alt={`Award ${i + 1}`} loading="lazy" />
            <p className="awards__text">{award.text}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ---- Main Home ----
const Home = () => (
  <div className="home">
    <Helmet>
      <title>
        RK Overseas | Glass Manufacturer & Exporter — Firozabad, India
      </title>
      <meta
        name="description"
        content="RK Overseas, Firozabad — manufacturer and exporter of handcrafted glass decorative accessories since 1997. Specializing in candle holders, vases, votives and Christmas ornaments."
      />
      <meta
        name="keywords"
        content="glass manufacturer firozabad, handcrafted glass exporter india, candle holders, vases, votives, christmas ornaments, RK Overseas"
      />
      <link rel="canonical" href="https://rkoverseasindia.com/" />
    </Helmet>
    <HeroSlider />
    <AboutSnippet />
    <StatsSection />
    <ProductsSection />
    <TestimonialsSection />
    <ContactMini />
    <FairsSection />
    <AwardsSection />
  </div>
);

export default Home;
